/**
 * Links from `viko setup` / `viko enroll` (`/setup#e=<CODE>.<secret>&i=<invite>`,
 * `/enroll#e=…`) and invite links (`/i/<code>`, `/setup#i=<code>`).
 *
 * The secrets ride in the URL fragment, which browsers never send to a server. On boot we move
 * them into sessionStorage (this tab only) and strip them from the address bar and history with
 * `history.replaceState`, so they don't linger in history, bookmarks or screenshots. They survive
 * a sign-in round trip and are cleared once used or after 20 minutes.
 */

export interface PendingLink {
  /** `<CODE>.<secret>` of a daemon's pending claim. */
  e?: string
  /** Invite code (26 Crockford characters). */
  i?: string
  /** Which page the link was for. */
  kind: 'setup' | 'enroll'
  at: number
}

const KEY = 'viko.pending-link'
const TTL_MS = 20 * 60_000
const E_RE = /^[0-9A-Za-z]{8}\.[A-Za-z0-9_-]{43}$/

export function normalizeInvite(raw: string): string | null {
  const c = raw.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1')
  return /^[0-9A-HJKMNP-TV-Z]{26}$/.test(c) ? c : null
}

/** `e` from a pasted terminal link or a bare `<CODE>.<secret>`. */
export function parseEnrollLink(raw: string): string | null {
  const t = raw.trim()
  const hash = t.includes('#') ? t.slice(t.indexOf('#') + 1) : ''
  const e = hash ? new URLSearchParams(hash).get('e') : t
  return e && E_RE.test(e) ? e : null
}

function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function readPending(now = Date.now()): PendingLink | null {
  try {
    const raw = storage()?.getItem(KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as PendingLink
    if (now - p.at > TTL_MS) {
      clearPending()
      return null
    }
    return p
  } catch {
    return null
  }
}

export function writePending(p: Omit<PendingLink, 'at'>) {
  try {
    storage()?.setItem(KEY, JSON.stringify({ ...p, at: Date.now() }))
  } catch {}
}

export function clearPending(field?: 'e' | 'i') {
  try {
    if (!field) return storage()?.removeItem(KEY)
    const p = readPending()
    if (!p) return
    delete p[field]
    if (!p.e && !p.i) storage()?.removeItem(KEY)
    else storage()?.setItem(KEY, JSON.stringify(p))
  } catch {}
}

/**
 * Run once before the router: move link secrets out of the URL. Returns the path the app should
 * show (e.g. `/i/<code>` becomes `/setup`).
 */
export function captureLink(loc: Location = window.location, hist: History = window.history) {
  const path = loc.pathname
  const inviteMatch = /^\/i\/([0-9A-Za-z-]{20,40})\/?$/.exec(path)
  if (inviteMatch?.[1]) {
    const i = normalizeInvite(inviteMatch[1])
    const prev = readPending()
    writePending({ ...(prev?.e ? { e: prev.e } : {}), ...(i ? { i } : {}), kind: 'setup' })
    hist.replaceState(hist.state, '', '/setup')
    return
  }
  if (path !== '/setup' && path !== '/enroll') return
  if (!loc.hash || loc.hash.length < 2) return
  const params = new URLSearchParams(loc.hash.slice(1))
  const e = params.get('e')
  const iRaw = params.get('i')
  const i = iRaw ? normalizeInvite(iRaw) : null
  if (e || i) {
    writePending({
      // Stored as given: a mangled link gets a clear "copy the whole link" error from the hub.
      ...(e ? { e: e.slice(0, 80) } : {}),
      ...(i ? { i } : {}),
      kind: path === '/setup' ? 'setup' : 'enroll',
    })
  }
  // Drop the fragment from the address bar and this history entry.
  hist.replaceState(hist.state, '', path + loc.search)
}
